import { useEffect } from 'react';

import type CardClient from '@atlaskit/link-provider/client';

import type { ManagedBlockConfiguration, ManagedBlockConfigurationUpdate } from '../configuration';

import { getSourceAppFromUrl, getSourceUrlsFromEntries, isValidSourceUrl } from './configuration';
import { replaceSourceEntry } from './source-entry-updates';
import {
	getInvalidReasonFromError,
	getInvalidReasonFromResponse,
	getResolvedApp,
} from './source-resolution';
import type { ManagedBlockSource } from './types';

type UseResolveSourcesArgs = {
	cardClient: CardClient | null;
	onChange: (update: ManagedBlockConfigurationUpdate) => void;
	sourceEntries: ManagedBlockSource[];
};

export const useResolveSources = ({
	cardClient,
	onChange,
	sourceEntries,
}: UseResolveSourcesArgs): void => {
	useEffect(() => {
		if (!cardClient) {
			return;
		}

		let cancelled = false;

		sourceEntries
			.filter((source) => source.status === 'resolving' && isValidSourceUrl(source.url))
			.forEach((source) => {
				cardClient
					.fetchData(source.url, false, 'inline')
					.then((response) => {
						if (cancelled) {
							return;
						}

						const invalidReason = getInvalidReasonFromResponse(response);
						onChange((currentConfiguration): ManagedBlockConfiguration => {
							const nextSourceEntries = invalidReason
								? replaceSourceEntry(currentConfiguration.sourceEntries, source.url, {
										app: getSourceAppFromUrl(source.url),
										invalidReason,
										status: 'invalid',
									})
								: replaceSourceEntry(currentConfiguration.sourceEntries, source.url, {
										app: getResolvedApp(source.url, response),
										invalidReason: undefined,
										status: 'resolved',
									});

							return {
								...currentConfiguration,
								sourceEntries: nextSourceEntries,
								sourceUrls: getSourceUrlsFromEntries(nextSourceEntries),
							};
						});
					})
					.catch((error: unknown) => {
						if (cancelled) {
							return;
						}

						onChange((currentConfiguration): ManagedBlockConfiguration => {
							const nextSourceEntries = replaceSourceEntry(
								currentConfiguration.sourceEntries,
								source.url,
								{
									app: getSourceAppFromUrl(source.url),
									invalidReason: getInvalidReasonFromError(error),
									status: 'invalid',
								},
							);

							return {
								...currentConfiguration,
								sourceEntries: nextSourceEntries,
								sourceUrls: getSourceUrlsFromEntries(nextSourceEntries),
							};
						});
					});
			});

		return () => {
			cancelled = true;
		};
	}, [cardClient, onChange, sourceEntries]);
};
